import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { confirmDialog, Dialog } from "@/components/ui";
import {
  ContentPage,
  ContentPageServices,
  CreateContentPageRequest,
  Media,
  SuperPageAssignment,
  SuperPageServices,
} from "@/services/bedrock";
import { VideoPageModalContent } from "@/containers/VideoPages/VideoPageModal/VideoPageModalContent";
import {
  ProgressState,
  VideoPageModalProgressOverlay,
} from "@/containers/VideoPages/VideoPageModal/VideoPageModalProgressOverlay";
import { SuperPageAssignmentDialogContent } from "@/containers/SuperPages";
import { useClearVideoPageCache, useInterval, useSocialCoachSession } from "@/utils";
import { CoachPostDialog, PlayerPostDialog, fromMedia } from "@/components";

type Props = {
  open: boolean;
  playerIdOverride?: number;
  initialObject?: { superPageAssignmentId: number } | { media: Media } | { videoPageId: number };
  showStats?: boolean;
  onClose: () => void;
  reviewId?: number | string;
  autoOpenShare?: boolean;
  formRequestId?: number | string;
};

export const VideoPageModal: React.FC<Props> = ({
  open,
  playerIdOverride,
  initialObject,
  showStats,
  onClose,
  reviewId,
  autoOpenShare,
  formRequestId,
}) => {
  const [completedSuperPageAssignment, setCompletedSuperPageAssignment] = useState(false);
  const [savingMedia, setSavingMedia] = useState(false);
  const [loadingInitialObject, setLoadingInitialObject] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const [assignment, setAssignment] = useState<SuperPageAssignment>();
  const [videoPage, setVideoPage] = useState<ContentPage>();
  const [postDialogMedia, setPostDialogMedia] = useState<Media | null>(null);

  const mediaSavedRef = useRef<Media | null>(null);

  const superPage = useMemo(() => {
    return videoPage?.superPage ?? assignment?.superPage;
  }, [assignment?.superPage, videoPage?.superPage]);

  const { isPlayer, playerId, coachId } = useSocialCoachSession();
  const effectivePlayerId = useMemo(() => {
    return assignment?.playerId || playerId || playerIdOverride;
  }, [assignment?.playerId, playerId, playerIdOverride]);

  const progressState = useMemo((): ProgressState | null => {
    if (!open || !initialObject) return null;

    if (loadingInitialObject) {
      return "loading";
    } else if (savingMedia) {
      return "creating";
    } else if (videoPage?.processingSteps) {
      const ready = videoPage.processingSteps.filter(s => s.type == "THUMBNAIL").every(s => s.status == "COMPLETE");
      if (!ready) return "processing";
    }
    if (completedSuperPageAssignment) return "assignmentCompleted";
    return null;
  }, [
    open,
    initialObject,
    loadingInitialObject,
    savingMedia,
    videoPage?.processingSteps,
    completedSuperPageAssignment,
  ]);

  const requestClose = useCallback(async () => {
    if (isFormDirty) {
      const confirmed = await confirmDialog({
        title: "Discard changes?",
        description: "You have unsaved changes. Are you sure you want to discard them?",
        confirmLabel: "Discard",
        cancelLabel: "Keep editing",
        destructive: true,
      });
      if (!confirmed) return;
    }
    onClose();
  }, [isFormDirty, onClose]);

  const savePageMedia = useCallback(
    (media: Media) => {
      setSavingMedia(true);
      const pageRequest: CreateContentPageRequest = {
        title: "",
        playerId: effectivePlayerId,
        superPageId: assignment?.superPageId || superPage?.id,
        coachId: coachId!,
        originalMedia: [media],
      };

      const savePromise = formRequestId
        ? ContentPageServices.createContentPageFromFormRequest({
            ...pageRequest,
            formRequestId: Number(formRequestId),
          })
        : reviewId
        ? ContentPageServices.createContentPageFromReview({
            ...pageRequest,
            reviewId: Number(reviewId),
          })
        : ContentPageServices.createContentPage(pageRequest);

      savePromise
        .then(page => {
          setVideoPage(page);
          setCompletedSuperPageAssignment(!!page.superPageId);
          if (page.superPageAssignment) {
            setAssignment({ ...page.superPageAssignment, superPage: page.superPage } as SuperPageAssignment);
          } else {
            setAssignment(undefined);
          }
          clearVideoPageCache({ superPages: true });
        })
        .catch(console.error)
        .finally(() => setSavingMedia(false));
    },
    [assignment, effectivePlayerId, coachId, superPage, reviewId, formRequestId]
  );

  const loadVideoPage = async (videoPageId: number) => {
    try {
      const videoPage = await ContentPageServices.getContentPage(videoPageId);
      setVideoPage(videoPage);
      if (videoPage.superPageAssignment) {
        setAssignment({ ...videoPage.superPageAssignment, superPage: videoPage.superPage } as SuperPageAssignment);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const loadSuperPageAssignment = async (superPageAssignmentId: number) => {
    try {
      const assignment = await SuperPageServices.getSuperPageAssignment(superPageAssignmentId, {
        superPage: ["videoScript", "templateContentPage"],
      });
      setAssignment(assignment);
      if (assignment.contentPageId) {
        await loadVideoPage(assignment.contentPageId);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!initialObject || !open) return;

    var initialObjectPromise: Promise<void> | undefined;
    if ("superPageAssignmentId" in initialObject) {
      initialObjectPromise = loadSuperPageAssignment(initialObject.superPageAssignmentId);
    } else if ("videoPageId" in initialObject) {
      initialObjectPromise = loadVideoPage(initialObject.videoPageId);
    } else {
      if (initialObject.media && mediaSavedRef.current !== initialObject.media) {
        mediaSavedRef.current = initialObject.media;
        savePageMedia(initialObject.media);
      }
    }

    if (initialObjectPromise) {
      setLoadingInitialObject(true);
      initialObjectPromise.finally(() => setLoadingInitialObject(false));
    }
  }, [initialObject, open]);

  useEffect(() => {
    if (open) return;
    setVideoPage(undefined);
    setAssignment(undefined);
    setCompletedSuperPageAssignment(false);
    setSavingMedia(false);
    setLoadingInitialObject(false);
    setPostDialogMedia(null);
    setIsFormDirty(false);
    mediaSavedRef.current = null;
  }, [open]);

  const handleVideoPageUpdated = (newVideoPage: ContentPage) => {
    const hasChangedOriginalMedia =
      newVideoPage.originalMedia[0] && newVideoPage.originalMedia[0].id !== videoPage?.originalMedia[0]?.id;

    if (!hasChangedOriginalMedia) {
      // We will clear cache after the video page has finished processing
      clearCaches(newVideoPage);
    }
    setVideoPage(newVideoPage);
  };

  const handleVideoPageDeleted = (videoPage: ContentPage) => {
    clearCaches(videoPage);
    onClose();
  };

  const { clearVideoPageCache } = useClearVideoPageCache();
  const handleUseDefaultVideo = (updatedAssignment: SuperPageAssignment) => {
    setAssignment(updatedAssignment);
    setCompletedSuperPageAssignment(true);
    if (updatedAssignment.contentPageId) {
      loadVideoPage(updatedAssignment.contentPageId);
      clearVideoPageCache({ superPages: true });
    }
  };

  const clearCaches = (videoPage: ContentPage | undefined | null) => {
    clearVideoPageCache({ superPages: !!videoPage?.superPageId });
  };

  useInterval(
    async () => {
      if (!videoPage) return;
      loadVideoPage(videoPage.id);
    },
    progressState === "processing" ? 1000 : undefined
  );

  const handleDismissAssignmentCreatedMessage = useCallback(() => {
    setCompletedSuperPageAssignment(false);
  }, []);

  const content = useMemo(() => {
    if (!open) {
      return null;
    } else if (progressState) {
      return (
        <VideoPageModalProgressOverlay
          progressState={progressState}
          onDismissAssignmentCreatedMessage={handleDismissAssignmentCreatedMessage}
        />
      );
    } else if (assignment && assignment.status !== "COMPLETED") {
      return (
        <SuperPageAssignmentDialogContent
          superPageAssignment={assignment}
          onMediaSelected={savePageMedia}
          onUseDefaultVideo={handleUseDefaultVideo}
        />
      );
    } else if (videoPage) {
      return (
        <VideoPageModalContent
          videoPage={videoPage}
          showStats={showStats}
          onVideoPageUpdated={handleVideoPageUpdated}
          onVideoPageDeleted={handleVideoPageDeleted}
          onOpenPostDialog={media => setPostDialogMedia(media)}
          onDirtyChange={setIsFormDirty}
          autoOpenShare={autoOpenShare}
          onDone={requestClose}
        />
      );
    } else {
      return null;
    }
  }, [open, progressState, assignment, videoPage, showStats, requestClose]);

  const [usesCompactDialog, setUsesCompactDialog] = useState(false);

  useEffect(() => {
    if (videoPage) {
      setUsesCompactDialog(false);
      return;
    } else if (assignment) {
      const superPage = assignment.superPage;
      if (
        !superPage?.videoScript &&
        !superPage?.templateContentPage?.originalMedia?.[0]?.uri &&
        assignment.status !== "COMPLETED"
      ) {
        setUsesCompactDialog(true);
      } else {
        setUsesCompactDialog(false);
      }
    }
  }, [assignment, videoPage]);

  return (
    <>
      <Dialog
        className={usesCompactDialog ? "min-h-67" : "min-h-170"}
        size={usesCompactDialog ? "md" : "2xl"}
        open={open && !postDialogMedia}
        onOpenChange={nextOpen => {
          if (!nextOpen) requestClose();
        }}
      >
        {content}
      </Dialog>

      {/* Post Video Dialogs */}
      {open &&
        postDialogMedia &&
        (isPlayer ? (
          <PlayerPostDialog
            open={!!postDialogMedia}
            handleClose={() => {
              setPostDialogMedia(null);
              requestClose();
            }}
            defaults={fromMedia(postDialogMedia)}
            playerId={videoPage?.playerId ?? undefined}
          />
        ) : (
          <CoachPostDialog
            open={!!postDialogMedia}
            handleClose={() => {
              setPostDialogMedia(null);
              requestClose();
            }}
            prompt={
              {
                promptId: undefined,
                message: "",
                post: {
                  type: postDialogMedia.category || "VIDEO",
                  media: [postDialogMedia],
                },
              } as any
            }
          />
        ))}
    </>
  );
};
